// Autocomplete search using local data array.

import * as React from "react";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { StackLayout } from "@progress/kendo-react-layout";
import { CPText } from "../../base";
import "./CPAutoCompleteStyles.scss";
import classNames from "classnames";
import { CPAutoCompleteProps } from "shared/components/types";
import useAutoCompleteAdapter from "shared/hooks/adapters/useAutoCompleteAdapter";
import { getFieldError } from "shared/helpers";

/**
 * Autocomplete / ComboBox that filters a **local** data array as the user types.
 * For remote / paginated data use `CPVirtualAutoComplete` instead.
 *
 * @example
 * ```tsx
 * const options = [
 *   { label: 'New York', value: 'NY' },
 *   { label: 'California', value: 'CA' },
 * ];
 *
 * <CPAutoComplete
 *   name="state"
 *   label="State"
 *   data={options}
 *   value={form.state}
 *   onChange={(item) => setForm({ ...form, state: item })}
 * />
 *
 * // Required with error
 * <CPAutoComplete
 *   name="city"
 *   label="City"
 *   required
 *   data={cityOptions}
 *   value={form.city}
 *   errorMsg={errors.city}
 *   onChange={(item) => setForm({ ...form, city: item })}
 * />
 * ```
 */
const CPAutoComplete: React.FC<CPAutoCompleteProps> = (props) => {
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    if (props.error != null) setDirty(false);
  }, [props.error]);

  const handleChange = (
    selectedItem?: import("shared/components/types").CPAutoCompleteDataItem,
  ) => {
    setDirty(true);
    props.onChange(selectedItem);
  };

  const adapter = useAutoCompleteAdapter({ ...props, onChange: handleChange });

  const renderPopupFooter = () => {
    return (
      <StackLayout style={{ backgroundColor: "#eee" }}>
        <CPText align="center" textStyle="caption">
          {adapter.virtual.total} result(s)
        </CPText>
      </StackLayout>
    );
  };

  return (
    <StackLayout orientation="vertical" gap={5}>
      <ComboBox
        {...adapter}
        className={classNames("cp-virtual-autocomplete-combobox", {
          error: adapter.showError(),
        })}
        fillMode={"solid"}
        label={props.label}
        name={props.name}
        id={props.name ?? props.label ?? "cp-autocomplete"}
        filterable={true}
        textField="label"
        dataItemKey="label"
        size={props.inputSize ?? "medium"}
        allowCustom={props.allowManualEntry ?? true}
        footer={renderPopupFooter()}
        suggest={true}
      />
      {adapter.showError() ||
      (!dirty && getFieldError(props.error, props.name ?? "") != null) ? (
        <CPText color="error" textStyle="caption">
          {props.errorMsg ??
            getFieldError(dirty ? undefined : props.error, props.name ?? "") ??
            `This field is required.`}
        </CPText>
      ) : (
        <CPText textStyle="caption">{props.helpText}</CPText>
      )}
    </StackLayout>
  );
};

export default CPAutoComplete;
